import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { Role } from '../models/Role'
import { roleSchema } from '../models/schema'
import {
  createRole as createRoleAPI,
  deleteRole as deleteRoleAPI,
  fetchRoleById,
  fetchRoles,
  updateRole as updateRoleAPI,
} from '../services/RoleService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<Role> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'role',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getRolesSuccess,
  setQuery: setRoleQuery,
  reset: resetRoleState,
  resetQueryAndIds: resetRoleQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Role }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectRoles = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectAllRoles = () =>
  createSelector([selectRawItems], (items) =>
    Object.entries(items).map((i) => i[1])
  )
export const selectSelectedRole = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectRoleById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectRolesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedRole =
  (role: Role | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Role>(role)))

export const getRoles = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchRoles(params),
    [roleSchema],
    slice.actions
  )

export const createRole = (role: Role) =>
  SBAPI.CreateDispatched(createRoleAPI(role), roleSchema, slice.actions)

export const getRoleWithId = (id: number) =>
  SBAPI.FetchDispatched<Role>(fetchRoleById(id), roleSchema, slice.actions)

export const updateRole = (role: Role) =>
  SBAPI.UpdateDispatched<Role>(updateRoleAPI(role), role, slice.actions)

export const deleteRole = (role: Role) =>
  SBAPI.DestroyDispatched<Role>(deleteRoleAPI(role), role, slice.actions)
